/**
 * Regular expression string for matching YouTube video IDs from various URL formats.
 *
 * Matches YouTube URLs including standard watch URLs, shortened URLs, embed URLs,
 * and YouTube Shorts URLs. The video ID is captured in the second match group.
 *
 * @example
 * ```typescript
 * const regex = new RegExp(youTubeIdRegExpString)
 * const match = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'.match(regex)
 * const videoId = match[2] // 'dQw4w9WgXcQ'
 * ```
 *
 * @see https://github.com/EduBeyond/youtube-url-to-embed
 */
export const youTubeIdRegExpString = `^.*(youtu.be\\/|v\\/|u\\/\\w\\/|embed\\/|shorts\\/|watch\\?v=|&v=)([^#&?]*).*`

/**
 * Regular expression string for matching Vimeo video IDs from various URL formats.
 *
 * Matches Vimeo URLs including standard watch URLs, player URLs, and embed URLs.
 * The video ID is captured in the first match group.
 *
 * @example
 * ```typescript
 * const regex = new RegExp(vimeoIdRegExpString)
 * const match = 'https://vimeo.com/123456789'.match(regex)
 * const videoId = match[1] // '123456789'
 * ```
 *
 * @see https://regexr.com/4lrm3
 */
export const vimeoIdRegExpString = `(?:http:|https:|)\\/\\/(?:player.|www.)?vimeo\\.com\\/(?:video\\/|embed\\/|watch\\?\\S*v=|v\\/)?(\\d*)`

/**
 * Extracts the YouTube video ID from a YouTube URL.
 *
 * Handles various YouTube URL formats including standard watch URLs,
 * shortened URLs, embed URLs, and YouTube Shorts URLs.
 *
 * @param url - The YouTube URL to extract the ID from
 * @returns The YouTube video ID if valid, null otherwise
 */
export const getYouTubeId = (url: string): null | string => {
  const regExp = new RegExp(youTubeIdRegExpString)
  const match = url.match(regExp)

  return match && match[2] && match[2].length === 11 ? match[2] : null
}

const YOUTUBE_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]
const YOUTUBE_SHORT_HOSTS = ['youtu.be', 'www.youtu.be']
const VIMEO_HOSTS = ['vimeo.com', 'www.vimeo.com']
const VIMEO_PLAYER_HOST = 'player.vimeo.com'

const URL_SCHEME = /^[a-z][\w+.-]*:/i
const YOUTUBE_ID = /^[\w-]{11}$/
const YOUTUBE_PATH_ID = /^\/(?:embed|shorts|live|v)\/([\w-]{11})(?:\/|$)/
// Share links use t=90, t=90s or t=1h2m3s; embed links use start=90
const YOUTUBE_TIME = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/
// Matches /ID, /ID/HASH (unlisted), /video/ID, /channels/NAME/ID,
// /groups/NAME/videos/ID and /album|showcase/ID/video/ID
const VIMEO_PATH =
  /^\/(?:(?:channels\/[\w-]+|groups\/[\w-]+\/videos|(?:album|showcase)\/\d+\/video|video)\/)?(\d+)(?:\/([^/]+))?\/?$/i
// Stored showcase URLs can have junk after the id, e.g. /video/ID&foo
const VIMEO_PLAYER_ID = /^\d+/
const VIMEO_ID = /^\d+$/
const VIMEO_HASH = /^[\da-f]+$/i

const parseUrl = (value: string) => {
  const trimmed = value.trim()
  // Users often paste links without a protocol, e.g. youtu.be/ID
  const withScheme = URL_SCHEME.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/\//, '')}`

  try {
    const url = new URL(withScheme)
    return ['http:', 'https:'].includes(url.protocol) ? url : null
  } catch {
    return null
  }
}

const parseYouTubeId = (url: URL) => {
  if (YOUTUBE_SHORT_HOSTS.includes(url.hostname)) {
    const id = url.pathname.split('/')[1] ?? ''
    return YOUTUBE_ID.test(id) ? id : null
  }

  if (url.pathname === '/watch') {
    const id = url.searchParams.get('v') ?? ''
    return YOUTUBE_ID.test(id) ? id : null
  }

  return url.pathname.match(YOUTUBE_PATH_ID)?.[1] ?? null
}

const getYouTubeStartSeconds = (url: URL) => {
  const time = url.searchParams.get('t') ?? url.searchParams.get('start')
  const [, hours = 0, minutes = 0, seconds = 0] =
    time?.match(YOUTUBE_TIME) ?? []
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds)
}

const getVimeoEmbedUrl = (id?: string, hash?: string | null) => {
  if (!id || !VIMEO_ID.test(id)) {
    return null
  }
  const query = hash && VIMEO_HASH.test(hash) ? `?h=${hash}` : ''
  return `https://player.vimeo.com/video/${id}${query}`
}

/**
 * Converts a YouTube or Vimeo share URL into an embeddable player URL,
 * keeping the YouTube start time and the Vimeo unlisted hash.
 * Hosts are matched exactly so arbitrary sites can't be framed.
 *
 * @param value - The video URL, with or without a protocol
 * @returns The iframe `src`, or `null` when the URL isn't a supported video
 */
const getVideoEmbedURL = (value?: string | null): string | null => {
  const url = value ? parseUrl(value) : null
  if (!url) {
    return null
  }

  if (
    YOUTUBE_HOSTS.includes(url.hostname) ||
    YOUTUBE_SHORT_HOSTS.includes(url.hostname)
  ) {
    const id = parseYouTubeId(url)
    if (!id) {
      return null
    }
    const start = getYouTubeStartSeconds(url)
    const query = start > 0 ? `?start=${start}` : ''
    return `https://www.youtube.com/embed/${id}${query}`
  }

  if (url.hostname === VIMEO_PLAYER_HOST) {
    const [, video, segment = ''] = url.pathname.split('/')
    return video === 'video'
      ? getVimeoEmbedUrl(
          segment.match(VIMEO_PLAYER_ID)?.[0],
          url.searchParams.get('h'),
        )
      : null
  }

  if (VIMEO_HOSTS.includes(url.hostname)) {
    const [, id, hash] = url.pathname.match(VIMEO_PATH) ?? []
    return getVimeoEmbedUrl(id, hash)
  }

  return null
}

export default getVideoEmbedURL
